import axios from "axios";
import { getAuthTokens, tokenNames } from "src/utils/getAuthTokens";
import { refreshToken as refreshTokenFunc } from "./Auth.service";

const API_ROUTE_ENV = {
  local: "http://localhost:3333",
  staging: "https://api-staging.padelyfe.com",
  production: "https://api.padelyfe.com"
}

type ApiEnvironment = keyof typeof API_ROUTE_ENV;

const nodeENV = process.env.REACT_APP_NODE_ENV;

export const getBaseURL = () => {
  let baseURL: string = API_ROUTE_ENV[nodeENV as ApiEnvironment];
  
  return baseURL;
};

const baseURL = getBaseURL();

const API = axios.create({
  baseURL: `${baseURL}/api/v1`,
});

API.interceptors.request.use(
  (req: any) => {
    const { token, refreshToken } = getAuthTokens();

    if (token) {
      req.headers = {
        ...req.headers,
        access_token: token,
        refresh_token: refreshToken,
      };
    }

    return req;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue: {
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}[] = [];

const processQueue = (
  error: any,
  token: string | null = null,
  refreshToken: string | null = null
) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve({ token, refreshToken });
    }
  });
  failedQueue = [];
};

API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const statusCode = error.response?.status;
    const errorMessage = error?.response?.data?.message;
    const isAuthRoute = originalRequest.url?.includes("/sign-in");

    if (statusCode === 401 && !originalRequest._retry) {
      const { token, refreshToken: refreshTok } = getAuthTokens();

      if (!token || !refreshTok) {
        localStorage.removeItem(tokenNames["token"]);
        localStorage.removeItem(tokenNames["refreshToken"]);
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: ({ token, refreshToken }) => {
              originalRequest.headers = {
                ...originalRequest.headers,
                access_token: token,
                refresh_token: refreshToken,
              };
              resolve(API(originalRequest));
            },
            reject,
          });
        });
      }

      isRefreshing = true;

      try {
        await refreshTokenFunc();
        const { token, refreshToken } = getAuthTokens();

        processQueue(null, token, refreshToken);

        originalRequest.headers = {
          ...originalRequest.headers,
          access_token: token,
          refresh_token: refreshToken,
        };

        return API(originalRequest);
      } catch (refreshError: any) {
        const message = refreshError?.response?.data?.message;

        if (message === "Invalid token") {
          localStorage.removeItem(tokenNames["token"]);
          localStorage.removeItem(tokenNames["refreshToken"]);

          window.location.reload();
        }

        processQueue(refreshError, null, null);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    } else if (errorMessage === "You are currently inactive" && !isAuthRoute) {
      localStorage.removeItem(tokenNames["token"]);
      localStorage.removeItem(tokenNames["refreshToken"]);

      window.location.reload();
    }

    return Promise.reject(error);
  }
);

export default API;